import { useMediaQuery } from '@mui/material';
import { Theme } from '@mui/system';
import { Form, Formik } from 'formik';
import { isEqual } from 'lodash-es';
import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { GRAY_DARK_SHADE_1, WHITE } from '~/Constants/Colors.constants';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import { makeStyles } from '~/UIKit/Style';
import { IItemCreationSectionInjectedProps, IItemCreationProps } from './ItemCreation.models';
import { useItemCreation } from './useItemCreation';

const useStyles = makeStyles(
  () => ({
    container: {
      flex: 1,
    },
  }),
  { name: 'ItemCreation' },
);
const ItemCreationContext = createContext<IItemCreationSectionInjectedProps<any>>(
  {} as IItemCreationSectionInjectedProps<any>,
);

export const ItemCreation = <T extends object>({
  modalTitle,
  toolbar,
  networkErrors,
  ItemCreationErrorsElement,
  children,
  initialValues,
  isLoading,
  validationSchema,
  validate,
  onSave,
  skipConfirmationModal = false,
  hideBackButton = false,
}: IItemCreationProps<T>) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const {
    Children,
    ErrorElement,
    FormikErrorsElement,
    Toolbar,
    blockNavigation,
    activeSectionIndex,
    setActiveSectionIndex,
    disabled,
    setDisabled,
  } = useItemCreation({
    toolbar,
    networkErrors,
    ItemCreationErrorsElement,
    children,
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {}}
      validate={validate}
      validationSchema={validationSchema}
      validateOnBlur
      validateOnMount
      enableReinitialize
    >
      {(formik) => {
        if (!isLoading && !skipConfirmationModal) {
          blockNavigation(!isEqual(formik.values, initialValues));
        }
        const ItemCreationContextElement = ItemCreationContext;
        const store: IItemCreationSectionInjectedProps<T> = {
          dirty: formik.dirty,
          errors: formik.errors,
          setFieldValue: formik.setFieldValue,
          values: formik.values,
          touched: formik.touched,
          setTouched: formik.setTouched,
          disabled,
          setDisabled,
          activeSectionIndex,
          setActiveSectionIndex,
          isLoading,
        };

        return (
          <ItemCreationContextElement.Provider value={store}>
            <PrivateLayout
              title={modalTitle}
              actionDisabled={isLoading}
              actionName={t('app.save')}
              onActionTaken={async () => {
                // mark all fields as touched
                Object.keys(formik.values).forEach((key) => {
                  formik.setFieldTouched(key, true, false);
                });
                if (!formik.isValid) {
                  return;
                }
                onSave(store);
              }}
              backgroundColor={isMobile ? GRAY_DARK_SHADE_1 : WHITE}
              hideBackButton={hideBackButton}
            >
              <Form>
                <div className={classes.container}>
                  <Toolbar />
                  <ErrorElement />
                  <FormikErrorsElement />
                  {Children.map((Child, index) => (
                    <div
                      style={{ display: activeSectionIndex === index ? '' : 'none' }}
                      key={`ICC_${index}`}
                    >
                      <Child {...store} />
                    </div>
                  ))}
                </div>
              </Form>
            </PrivateLayout>
          </ItemCreationContextElement.Provider>
        );
      }}
    </Formik>
  );
};

export const useItemCreationContext = () => useContext(ItemCreationContext);
